import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { FaIcon } from '../../common/Icons.jsx';


const rowStyle = {     
  flex: '1 1 auto',
  display: 'flex',
  height: 23,
  alignItems: 'center',
  paddingLeft: 'var(--ag-widget-container-horizontal-padding)',
  paddingRight: 'var(--ag-widget-container-horizontal-padding)',
  justifyContent: 'space-between',
  width: '100%'
 };

const CONTROLS_COLUMN_ID_PREFIX = 'ag-Grid-SelectionColumn'

const useReordeableColumns = (onReorder, handleEdit, api, searchText= '') => {
  const [columns, setColumns] = useState(api.getAllGridColumns());
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dropTargetIndex, setDropTargetIndex] = useState(null);

  const updateColumns = (api) => {
    setColumns(api.getAllGridColumns());
  };

  const handleReorder = useCallback(({api}) => {
    const idOrderMap = Object.fromEntries(api.getAllGridColumns().filter(v => v.colId != CONTROLS_COLUMN_ID_PREFIX).map((c, index) => [c.colDef.field, index]))
    onReorder(idOrderMap);
    updateColumns(api);
  }, [onReorder])

  const handleColumnChange = useCallback(({api}) => {
    updateColumns(api);
  }, [])

  useEffect(() => {
      api.addEventListener('columnMoved', handleReorder);
      api.addEventListener('gridColumnsChanged', handleColumnChange);
  
      return () => {
        if (!api.isDestroyed()){
          api.removeEventListener('gridColumnsChanged', handleColumnChange)
          api.removeEventListener('columnMoved', handleReorder) 
        }
      }
  }, [handleReorder, handleColumnChange])

  const handleDragStart = (event, columnIndex) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', columnIndex);
    setDraggedColumn(columnIndex);
  };

  const handleDragEnter = useCallback(
    (event, targetIndex) => {
      event.preventDefault();
      if (draggedColumn !== targetIndex) {
        setDropTargetIndex(targetIndex);
      }
    },
    [draggedColumn]
  );

  const handleDragOver = (event, idx) => {
    event.preventDefault();
    setDropTargetIndex(draggedColumn !== idx ? idx : null)
  };

  const handleDragLeave = useCallback(() => {
    setDropTargetIndex(null);
  }, []);

  const handleDrop = useCallback(
    (event, targetIndex) => {
      event.preventDefault();
      if (draggedColumn !== null) {
        const movedColumn = columns[draggedColumn]
        api.moveColumns([movedColumn.colDef.field], targetIndex); // Move the column in the API
        setDraggedColumn(null);
        setDropTargetIndex(null);      
      }
    },
    [columns, api, draggedColumn]
  );

  const orderableColumns = useMemo(() => {
    // Filter columns based on search
    var filteredColumns = columns;
    if (searchText) {
      const q = searchText.toLowerCase();
      filteredColumns = columns.filter(c => (c?.colDef?.cellRendererParams?.original?.name||'').toLowerCase().includes(q))
    }

    return filteredColumns.map((column, idx) => {
      const colDef = column.colDef;
      if (colDef.suppressColumnsToolPanel) return null;

      return (
        <div
          className="list-hover-action"
          style={{
            ...rowStyle,
            ...(idx === dropTargetIndex ? {backgroundColor: 'lightgray'} : {}),
            display: column.colId == CONTROLS_COLUMN_ID_PREFIX ? 'none' : 'flex'
          }}
          draggable
          onDragStart={(e) => handleDragStart(e, idx)}
          onDragEnter={(e) => handleDragEnter(e, idx)}
          onDragOver={(e) => handleDragOver(e, idx)}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, idx)}
          key={`ordeable-col-${colDef.field}`}
        >
          <div style={{display: 'flex', maxWidth: 'calc(100% - 1.25rem)'}}>
            <span className="ag-icon ag-icon-grip ag-drag-handle ag-column-select-column-drag-handle" role="presentation"></span>
            <span className='text-truncate mr-1'>{colDef.headerName}</span>
          </div>
          <span>
            <FaIcon type='cog' style={{cursor: 'pointer'}} onClick={() => handleEdit(colDef.field)} />
          </span>
        </div>
      );
    });
  }, [columns, dropTargetIndex, handleDragStart, handleDragEnter, handleDragOver, handleDragLeave, handleDrop, searchText]);

  return {
    columns,
    orderableColumns
  }
}

export default useReordeableColumns;